/*
 * Lightweight i18n — no external deps. Covers the visible marketing surface + chrome.
 * `t(key)` returns the translation for the current language, falling back to English.
 */
import { createContext, useContext, useEffect, useState, useMemo } from "react";

export const LANGUAGES = [
  { code: "en", label: "English",   native: "English" },
  { code: "hi", label: "Hindi",     native: "हिंदी" },
  { code: "bn", label: "Bengali",   native: "বাংলা" },
  { code: "ta", label: "Tamil",     native: "தமிழ்" },
  { code: "te", label: "Telugu",    native: "తెలుగు" },
  { code: "mr", label: "Marathi",   native: "मराठी" },
  { code: "kn", label: "Kannada",   native: "ಕನ್ನಡ" },
  { code: "gu", label: "Gujarati",  native: "ગુજરાતી" },
];

// English canonical (fallback for every key).
const DICT = {
  en: {
    "header.tagline": "Your Claim. Our Expertise.",
    "nav.file": "File a Claim", "nav.track": "Track Claim", "nav.enterprise": "Enterprise",
    "nav.emergency": "Emergency", "nav.knowledge": "Knowledge",
    "cta.signIn": "Sign in", "cta.startClaim": "Start a claim", "cta.forEnterprises": "For enterprises",
    "cta.fileClaim": "File a Claim",

    "hero.pill": "AI-native · SOC 2-ready · Trusted by 25+ insurers",
    "hero.title.a": "The", "hero.title.b": "AI operating system", "hero.title.c": "for motor insurance", "hero.title.d": "claims.",
    "hero.subline": "File, track, and settle claims 5× faster with AI damage estimation, cashless workshop coordination, and a personal claim manager. Built independently — we work for policyholders, not insurers.",
    "hero.badge.assisted": "100,000+ claims assisted",
    "hero.badge.settlement": "12-day avg settlement",
    "hero.badge.garages": "12,000+ cashless garages",

    "traction.eyebrow": "Traction",
    "traction.title": "The numbers behind India's fastest-growing claim platform.",
    "traction.claims": "Claims assisted",
    "traction.settlements": "Settlements paid",
    "traction.garages": "Cashless garages",
    "traction.partners": "Insurance partners",
    "traction.cities": "Cities live",

    "howitworks.eyebrow": "How it works",
    "howitworks.title": "Four steps. Zero paperwork trauma.",

    "problem.eyebrow": "The Problem · The Fix",
    "problem.beforeUs": "Before us",
    "problem.afterUs": "After miclaim",

    "chat.title": "Chat with your claim manager",
    "chat.placeholder": "Type a message…",
    "chat.send": "Send",
    "chat.empty": "No messages yet. Say hi 👋",

    "gallery.title": "Claim photos",
    "gallery.subtitle": "Every photo you've uploaded on this claim",
    "gallery.empty": "No photos yet. Upload accident photos from the Document Vault.",

    "footer.rights": "All rights reserved.",
  },
  hi: {
    "header.tagline": "आपका दावा. हमारी विशेषज्ञता.",
    "nav.file": "दावा दर्ज करें", "nav.track": "दावा ट्रैक करें", "nav.enterprise": "एंटरप्राइज़",
    "nav.emergency": "आपातकालीन", "nav.knowledge": "जानकारी",
    "cta.signIn": "साइन इन", "cta.startClaim": "दावा शुरू करें", "cta.forEnterprises": "उद्यमों के लिए",
    "cta.fileClaim": "दावा दर्ज करें",

    "hero.pill": "AI-आधारित · SOC 2-तैयार · 25+ बीमाकर्ताओं द्वारा भरोसेमंद",
    "hero.title.a": "मोटर बीमा दावों के लिए", "hero.title.b": "AI ऑपरेटिंग सिस्टम", "hero.title.c": "", "hero.title.d": ".",
    "hero.subline": "AI क्षति आकलन, कैशलेस वर्कशॉप समन्वय और एक व्यक्तिगत क्लेम मैनेजर के साथ दावे 5× तेज़ी से दर्ज करें, ट्रैक करें और सेटल करें। स्वतंत्र — हम पॉलिसीधारकों के लिए काम करते हैं, बीमाकर्ताओं के लिए नहीं।",
    "hero.badge.assisted": "1,00,000+ दावों में सहायता",
    "hero.badge.settlement": "12-दिन औसत निपटान",
    "hero.badge.garages": "12,000+ कैशलेस गैरेज",

    "traction.eyebrow": "प्रगति",
    "traction.title": "भारत के सबसे तेज़ी से बढ़ते क्लेम प्लेटफ़ॉर्म के आँकड़े।",
    "traction.claims": "सहायता किए गए दावे",
    "traction.settlements": "भुगतान किए गए निपटान",
    "traction.garages": "कैशलेस गैरेज",
    "traction.partners": "बीमा साझेदार",
    "traction.cities": "सक्रिय शहर",

    "howitworks.eyebrow": "यह कैसे काम करता है",
    "howitworks.title": "चार चरण। शून्य कागज़ी परेशानी।",

    "problem.eyebrow": "समस्या · समाधान",
    "problem.beforeUs": "हमसे पहले",
    "problem.afterUs": "miclaim के बाद",

    "chat.title": "अपने क्लेम मैनेजर से चैट करें",
    "chat.placeholder": "संदेश लिखें…",
    "chat.send": "भेजें",
    "chat.empty": "अभी कोई संदेश नहीं है। नमस्ते कहें 👋",

    "gallery.title": "दावा तस्वीरें",
    "gallery.subtitle": "इस दावे पर आपकी अपलोड की हुई हर तस्वीर",
    "gallery.empty": "अभी कोई तस्वीर नहीं है। दस्तावेज़ वॉल्ट से दुर्घटना की तस्वीरें अपलोड करें।",

    "footer.rights": "सर्वाधिकार सुरक्षित।",
  },
  bn: {
    "header.tagline": "আপনার দাবি. আমাদের দক্ষতা.",
    "nav.file": "দাবি করুন", "nav.track": "দাবি ট্র্যাক", "nav.enterprise": "এন্টারপ্রাইজ",
    "nav.emergency": "জরুরি", "nav.knowledge": "জ্ঞান",
    "cta.signIn": "সাইন ইন", "cta.startClaim": "দাবি শুরু করুন", "cta.forEnterprises": "এন্টারপ্রাইজের জন্য",
    "cta.fileClaim": "দাবি করুন",
    "hero.pill": "AI-স্থানীয় · SOC 2-প্রস্তুত · 25+ বীমাকারী দ্বারা বিশ্বস্ত",
    "hero.title.a": "মোটর বীমা দাবির জন্য", "hero.title.b": "AI অপারেটিং সিস্টেম", "hero.title.c": "", "hero.title.d": ".",
    "hero.subline": "AI ক্ষতি নিরূপণ, ক্যাশলেস ওয়ার্কশপ সমন্বয় এবং একজন ব্যক্তিগত ক্লেম ম্যানেজার সহ দাবি 5× দ্রুত ফাইল করুন, ট্র্যাক করুন এবং নিষ্পত্তি করুন।",
    "hero.badge.assisted": "1,00,000+ দাবিতে সহায়তা",
    "hero.badge.settlement": "12-দিন গড় নিষ্পত্তি",
    "hero.badge.garages": "12,000+ ক্যাশলেস গ্যারেজ",
    "traction.eyebrow": "অগ্রগতি",
    "traction.title": "ভারতের দ্রুততম বর্ধনশীল ক্লেম প্ল্যাটফর্মের পরিসংখ্যান।",
    "traction.claims": "সহায়তাকৃত দাবি", "traction.settlements": "প্রদত্ত নিষ্পত্তি",
    "traction.garages": "ক্যাশলেস গ্যারেজ", "traction.partners": "বীমা অংশীদার", "traction.cities": "সক্রিয় শহর",
    "howitworks.eyebrow": "এটি কীভাবে কাজ করে", "howitworks.title": "চার ধাপ। শূন্য কাগজপত্রের ঝামেলা।",
    "problem.eyebrow": "সমস্যা · সমাধান", "problem.beforeUs": "আমাদের আগে", "problem.afterUs": "miclaim এর পরে",
    "chat.title": "আপনার ক্লেম ম্যানেজারের সাথে চ্যাট করুন", "chat.placeholder": "একটি বার্তা টাইপ করুন…",
    "chat.send": "পাঠান", "chat.empty": "এখনো কোন বার্তা নেই। হাই বলুন 👋",
    "gallery.title": "দাবি ছবি", "gallery.subtitle": "এই দাবিতে আপনার আপলোড করা প্রতিটি ছবি",
    "gallery.empty": "এখনো কোন ছবি নেই।", "footer.rights": "সর্বস্বত্ব সংরক্ষিত।",
  },
  ta: {
    "header.tagline": "உங்கள் உரிமைகோரல். எங்கள் நிபுணத்துவம்.",
    "nav.file": "உரிமைகோரல் தாக்கல்", "nav.track": "உரிமைகோரலைக் கண்காணி", "nav.enterprise": "நிறுவனம்",
    "nav.emergency": "அவசர", "nav.knowledge": "அறிவு",
    "cta.signIn": "உள்நுழை", "cta.startClaim": "உரிமைகோரலைத் தொடங்கு", "cta.forEnterprises": "நிறுவனங்களுக்கு",
    "cta.fileClaim": "உரிமைகோரல் தாக்கல்",
    "hero.pill": "AI-சொந்த · SOC 2-தயார் · 25+ காப்பீட்டாளர்களால் நம்பப்படுகிறது",
    "hero.title.a": "மோட்டார் காப்பீட்டு உரிமைகோரல்களுக்கான", "hero.title.b": "AI இயக்க முறைமை", "hero.title.c": "", "hero.title.d": ".",
    "hero.subline": "AI சேத மதிப்பீடு, பணமில்லா பட்டறை ஒருங்கிணைப்பு மற்றும் ஒரு தனிப்பட்ட உரிமைகோரல் மேலாளருடன் உரிமைகோரல்களை 5× வேகமாக தாக்கல் செய்யுங்கள்.",
    "hero.badge.assisted": "1,00,000+ உரிமைகோரல்களுக்கு உதவி",
    "hero.badge.settlement": "12-நாள் சராசரி தீர்வு", "hero.badge.garages": "12,000+ பணமில்லா பட்டறைகள்",
    "traction.eyebrow": "வளர்ச்சி", "traction.title": "இந்தியாவின் வேகமாக வளர்ந்து வரும் தளத்தின் புள்ளிவிவரங்கள்.",
    "traction.claims": "உதவப்பட்ட உரிமைகோரல்கள்", "traction.settlements": "செலுத்தப்பட்ட தீர்வுகள்",
    "traction.garages": "பணமில்லா பட்டறைகள்", "traction.partners": "காப்பீட்டு பங்குதாரர்கள்", "traction.cities": "நேரடி நகரங்கள்",
    "howitworks.eyebrow": "இது எப்படி வேலை செய்கிறது", "howitworks.title": "நான்கு படிகள். பூஜ்ஜிய காகிதப்பணி.",
    "problem.eyebrow": "பிரச்சனை · தீர்வு", "problem.beforeUs": "எங்களுக்கு முன்", "problem.afterUs": "miclaim க்குப் பிறகு",
    "chat.title": "உங்கள் மேலாளருடன் அரட்டை", "chat.placeholder": "செய்தியை உள்ளிடவும்…",
    "chat.send": "அனுப்பு", "chat.empty": "இன்னும் செய்திகள் இல்லை. வணக்கம் சொல்லுங்கள் 👋",
    "gallery.title": "உரிமைகோரல் புகைப்படங்கள்", "gallery.subtitle": "இந்த உரிமைகோரலில் நீங்கள் பதிவேற்றிய ஒவ்வொரு படம்",
    "gallery.empty": "இன்னும் புகைப்படங்கள் இல்லை.", "footer.rights": "அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை.",
  },
  te: {
    "header.tagline": "మీ దావా. మా నైపుణ్యం.",
    "nav.file": "దావా ఫైల్ చేయండి", "nav.track": "దావాను ట్రాక్ చేయండి", "nav.enterprise": "ఎంటర్ప్రైజ్",
    "nav.emergency": "అత్యవసరం", "nav.knowledge": "జ్ఞానం",
    "cta.signIn": "సైన్ ఇన్", "cta.startClaim": "దావా ప్రారంభించండి", "cta.forEnterprises": "సంస్థల కోసం",
    "cta.fileClaim": "దావా ఫైల్ చేయండి",
    "hero.pill": "AI-స్థానిక · 25+ బీమా సంస్థలు నమ్ముతున్నాయి",
    "hero.title.a": "మోటార్ ఇన్సూరెన్స్ దావాలకు", "hero.title.b": "AI ఆపరేటింగ్ సిస్టమ్", "hero.title.c": "", "hero.title.d": ".",
    "hero.subline": "AI నష్ట అంచనా, నగదు రహిత వర్క్‌షాప్ మరియు వ్యక్తిగత క్లెయిమ్ మేనేజర్‌తో 5× వేగంగా దావాలు.",
    "hero.badge.assisted": "1,00,000+ దావాలు", "hero.badge.settlement": "12-రోజుల సెటిల్‌మెంట్",
    "hero.badge.garages": "12,000+ నగదు రహిత గ్యారేజీలు",
    "traction.eyebrow": "వృద్ధి", "traction.title": "భారత్‌లో అత్యంత వేగంగా వృద్ధి చెందుతున్న ప్లాట్‌ఫారమ్.",
    "traction.claims": "సహాయం చేయబడ్డ దావాలు", "traction.settlements": "చెల్లించిన సెటిల్‌మెంట్లు",
    "traction.garages": "నగదు రహిత గ్యారేజీలు", "traction.partners": "బీమా భాగస్వాములు", "traction.cities": "ప్రత్యక్ష నగరాలు",
    "howitworks.eyebrow": "ఇది ఎలా పనిచేస్తుంది", "howitworks.title": "నాలుగు దశలు. సున్నా కాగితపు పని.",
    "problem.eyebrow": "సమస్య · పరిష్కారం", "problem.beforeUs": "మాకు ముందు", "problem.afterUs": "miclaim తర్వాత",
    "chat.title": "మీ మేనేజర్‌తో చాట్", "chat.placeholder": "సందేశం టైప్ చేయండి…",
    "chat.send": "పంపు", "chat.empty": "ఇంకా సందేశాలు లేవు.", "gallery.title": "దావా ఫోటోలు",
    "gallery.subtitle": "మీరు అప్‌లోడ్ చేసిన ప్రతి ఫోటో", "gallery.empty": "ఇంకా ఫోటోలు లేవు.",
    "footer.rights": "అన్ని హక్కులు రిజర్వ్ చేయబడ్డాయి.",
  },
  mr: {
    "header.tagline": "तुमचा दावा. आमची तज्ज्ञता.",
    "nav.file": "दावा दाखल करा", "nav.track": "दाव्याचा मागोवा", "nav.enterprise": "एंटरप्राइझ",
    "nav.emergency": "आणीबाणी", "nav.knowledge": "माहिती",
    "cta.signIn": "साइन इन", "cta.startClaim": "दावा सुरू करा", "cta.forEnterprises": "एंटरप्राइझसाठी",
    "cta.fileClaim": "दावा दाखल करा",
    "hero.pill": "AI-मूळ · 25+ विमा कंपन्यांचा विश्वास",
    "hero.title.a": "मोटर विमा दाव्यांसाठी", "hero.title.b": "AI ऑपरेटिंग सिस्टम", "hero.title.c": "", "hero.title.d": ".",
    "hero.subline": "AI नुकसान अंदाज आणि वैयक्तिक क्लेम मॅनेजरसह दावे 5× जलद.",
    "hero.badge.assisted": "1,00,000+ दाव्यांना मदत", "hero.badge.settlement": "12-दिवस सरासरी सेटलमेंट",
    "hero.badge.garages": "12,000+ कॅशलेस गॅरेजेस",
    "traction.eyebrow": "प्रगती", "traction.title": "भारतातील सर्वात वेगाने वाढणारे प्लॅटफॉर्म.",
    "traction.claims": "मदत केलेले दावे", "traction.settlements": "दिलेले सेटलमेंट",
    "traction.garages": "कॅशलेस गॅरेजेस", "traction.partners": "विमा भागीदार", "traction.cities": "थेट शहरे",
    "howitworks.eyebrow": "हे कसे कार्य करते", "howitworks.title": "चार पायऱ्या. शून्य कागदपत्रे.",
    "problem.eyebrow": "समस्या · उपाय", "problem.beforeUs": "आमच्या आधी", "problem.afterUs": "miclaim नंतर",
    "chat.title": "तुमच्या मॅनेजरशी चॅट करा", "chat.placeholder": "संदेश टाइप करा…",
    "chat.send": "पाठवा", "chat.empty": "अजून संदेश नाहीत.", "gallery.title": "दावा फोटो",
    "gallery.subtitle": "तुम्ही अपलोड केलेला प्रत्येक फोटो", "gallery.empty": "अजून फोटो नाहीत.",
    "footer.rights": "सर्व हक्क राखीव.",
  },
  kn: {
    "header.tagline": "ನಿಮ್ಮ ಕ್ಲೈಮ್. ನಮ್ಮ ಪರಿಣತಿ.",
    "nav.file": "ಕ್ಲೈಮ್ ಸಲ್ಲಿಸಿ", "nav.track": "ಕ್ಲೈಮ್ ಟ್ರ್ಯಾಕ್", "nav.enterprise": "ಎಂಟರ್ಪ್ರೈಸ್",
    "nav.emergency": "ತುರ್ತು", "nav.knowledge": "ಜ್ಞಾನ",
    "cta.signIn": "ಸೈನ್ ಇನ್", "cta.startClaim": "ಕ್ಲೈಮ್ ಆರಂಭಿಸಿ", "cta.forEnterprises": "ಸಂಸ್ಥೆಗಳಿಗಾಗಿ",
    "cta.fileClaim": "ಕ್ಲೈಮ್ ಸಲ್ಲಿಸಿ",
    "hero.pill": "AI-ಸ್ಥಳೀಯ · 25+ ವಿಮಾ ಸಂಸ್ಥೆಗಳ ವಿಶ್ವಾಸ",
    "hero.title.a": "ಮೋಟಾರ್ ಇನ್ಶುರೆನ್ಸ್ ಕ್ಲೈಮ್‌ಗಳಿಗಾಗಿ", "hero.title.b": "AI ಆಪರೇಟಿಂಗ್ ಸಿಸ್ಟಮ್", "hero.title.c": "", "hero.title.d": ".",
    "hero.subline": "AI ಹಾನಿ ಅಂದಾಜು ಮತ್ತು ವೈಯಕ್ತಿಕ ಕ್ಲೈಮ್ ಮ್ಯಾನೇಜರ್ ಜೊತೆಗೆ 5× ವೇಗವಾಗಿ ಕ್ಲೈಮ್‌ಗಳು.",
    "hero.badge.assisted": "1,00,000+ ಕ್ಲೈಮ್‌ಗಳಿಗೆ ಸಹಾಯ", "hero.badge.settlement": "12-ದಿನ ಸರಾಸರಿ ಇತ್ಯರ್ಥ",
    "hero.badge.garages": "12,000+ ನಗದು-ಮುಕ್ತ ಗ್ಯಾರೇಜ್‌ಗಳು",
    "traction.eyebrow": "ಪ್ರಗತಿ", "traction.title": "ಭಾರತದ ಅತಿ ವೇಗವಾಗಿ ಬೆಳೆಯುತ್ತಿರುವ ವೇದಿಕೆ.",
    "traction.claims": "ಸಹಾಯಿತ ಕ್ಲೈಮ್‌ಗಳು", "traction.settlements": "ಪಾವತಿಸಿದ ಇತ್ಯರ್ಥಗಳು",
    "traction.garages": "ನಗದು-ಮುಕ್ತ ಗ್ಯಾರೇಜ್‌ಗಳು", "traction.partners": "ವಿಮಾ ಪಾಲುದಾರರು", "traction.cities": "ನೇರ ನಗರಗಳು",
    "howitworks.eyebrow": "ಇದು ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತದೆ", "howitworks.title": "ನಾಲ್ಕು ಹಂತಗಳು. ಶೂನ್ಯ ಕಾಗದಪತ್ರ.",
    "problem.eyebrow": "ಸಮಸ್ಯೆ · ಪರಿಹಾರ", "problem.beforeUs": "ನಮಗಿಂತ ಮೊದಲು", "problem.afterUs": "miclaim ನಂತರ",
    "chat.title": "ನಿಮ್ಮ ಮ್ಯಾನೇಜರ್‌ನೊಂದಿಗೆ ಚಾಟ್", "chat.placeholder": "ಸಂದೇಶ ಟೈಪ್ ಮಾಡಿ…",
    "chat.send": "ಕಳುಹಿಸಿ", "chat.empty": "ಇನ್ನೂ ಸಂದೇಶಗಳಿಲ್ಲ.", "gallery.title": "ಕ್ಲೈಮ್ ಫೋಟೋಗಳು",
    "gallery.subtitle": "ನೀವು ಅಪ್‌ಲೋಡ್ ಮಾಡಿದ ಪ್ರತಿ ಫೋಟೋ", "gallery.empty": "ಇನ್ನೂ ಫೋಟೋಗಳಿಲ್ಲ.",
    "footer.rights": "ಎಲ್ಲಾ ಹಕ್ಕುಗಳು ಕಾಯ್ದಿರಿಸಿವೆ.",
  },
  gu: {
    "header.tagline": "તમારો દાવો. અમારી નિપુણતા.",
    "nav.file": "દાવો દાખલ કરો", "nav.track": "દાવો ટ્રૅક કરો", "nav.enterprise": "એન્ટરપ્રાઈઝ",
    "nav.emergency": "કટોકટી", "nav.knowledge": "જ્ઞાન",
    "cta.signIn": "સાઇન ઇન", "cta.startClaim": "દાવો શરૂ કરો", "cta.forEnterprises": "એન્ટરપ્રાઈઝ માટે",
    "cta.fileClaim": "દાવો દાખલ કરો",
    "hero.pill": "AI-મૂળ · 25+ વીમા કંપનીઓનો વિશ્વાસ",
    "hero.title.a": "મોટર વીમા દાવાઓ માટે", "hero.title.b": "AI ઑપરેટિંગ સિસ્ટમ", "hero.title.c": "", "hero.title.d": ".",
    "hero.subline": "AI નુકસાન અંદાજ અને વ્યક્તિગત ક્લેમ મેનેજર સાથે 5× ઝડપી દાવા.",
    "hero.badge.assisted": "1,00,000+ દાવાઓમાં મદદ", "hero.badge.settlement": "12-દિવસ સરેરાશ સેટલમેન્ટ",
    "hero.badge.garages": "12,000+ કૅશલેસ ગેરેજ",
    "traction.eyebrow": "પ્રગતિ", "traction.title": "ભારતનું સૌથી ઝડપી વધતું પ્લેટફોર્મ.",
    "traction.claims": "મદદ કરેલા દાવા", "traction.settlements": "ચૂકવેલા સેટલમેન્ટ",
    "traction.garages": "કૅશલેસ ગેરેજ", "traction.partners": "વીમા ભાગીદારો", "traction.cities": "લાઇવ શહેરો",
    "howitworks.eyebrow": "તે કેવી રીતે કામ કરે છે", "howitworks.title": "ચાર પગલાં. શૂન્ય કાગળકામ.",
    "problem.eyebrow": "સમસ્યા · ઉકેલ", "problem.beforeUs": "અમારા પહેલા", "problem.afterUs": "miclaim પછી",
    "chat.title": "તમારા મેનેજર સાથે ચેટ", "chat.placeholder": "સંદેશ ટાઈપ કરો…",
    "chat.send": "મોકલો", "chat.empty": "હજુ કોઈ સંદેશા નથી.", "gallery.title": "દાવો ફોટા",
    "gallery.subtitle": "તમે અપલોડ કરેલો દરેક ફોટો", "gallery.empty": "હજુ કોઈ ફોટા નથી.",
    "footer.rights": "તમામ હકો અનામત.",
  },
};

const I18nContext = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() =>
    (typeof localStorage !== "undefined" && localStorage.getItem("miclaim_lang")) || "en"
  );

  useEffect(() => {
    if (typeof localStorage !== "undefined") localStorage.setItem("miclaim_lang", lang);
    if (typeof document !== "undefined") document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  const setLang = (v) => setLangState(LANGUAGES.find(l => l.code === v) ? v : "en");
  const t = useMemo(() => {
    const bank = DICT[lang] || {};
    const en = DICT.en;
    return (key) => bank[key] ?? en[key] ?? key;
  }, [lang]);

  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
